import { useEffect, useState } from 'react';

// Smaller drops of the visible area are the browser chrome (Safari's address bar), not a keyboard.
const KEYBOARD_MIN_HEIGHT = 150;
// Moving from one field to the next blurs the first before focusing the second: wait that gap out
// so a fixed bar does not flash above a keyboard that never closed.
const FOCUS_SETTLE_MS = 100;

const NON_TEXT_INPUT_TYPES = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'hidden',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
]);

export const isTextEntryElement = (element: Element | null): boolean => {
  if (!element) return false;
  if (element instanceof HTMLTextAreaElement) return !element.readOnly;
  if (element instanceof HTMLInputElement) return !element.readOnly && !NON_TEXT_INPUT_TYPES.has(element.type);
  // Third-party fields render in iframes (Stripe card number, expiry, CVC): the page only sees the frame.
  if (element instanceof HTMLIFrameElement) return true;
  return element instanceof HTMLElement && element.isContentEditable === true;
};

/**
 * True while the on-screen keyboard of a touch device is open, so fixed bars (a wizard's
 * "Continua"/"Indietro" footer) can step aside instead of eating half of what is left of the screen.
 *
 * Needs both signals: a focused text field alone is also an iPad with a hardware keyboard, and a
 * shorter visible area alone is also Safari's address bar collapsing. Always false on desktop.
 */
export const useIsVirtualKeyboardOpen = (): boolean => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport || !window.matchMedia('(pointer: coarse)').matches) return;

    // Full height of the page with no keyboard. Browsers that shrink the layout viewport with the
    // keyboard (older Android, interactive-widget=resizes-content) move innerHeight too, so it is
    // remembered from a moment with no field focused, and reset when the width (orientation) changes.
    let fullHeight = window.innerHeight;
    let fullHeightWidth = window.innerWidth;
    let settleTimer: ReturnType<typeof setTimeout> | undefined;

    const update = () => {
      const isTyping = isTextEntryElement(document.activeElement);
      if (window.innerWidth !== fullHeightWidth || !isTyping) {
        fullHeight = window.innerHeight;
        fullHeightWidth = window.innerWidth;
      }
      // height * scale keeps a pinch zoom from reading as a keyboard.
      setIsOpen(isTyping && fullHeight - viewport.height * viewport.scale >= KEYBOARD_MIN_HEIGHT);
    };

    const onFocusOut = () => {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(update, FOCUS_SETTLE_MS);
    };

    document.addEventListener('focusin', update);
    document.addEventListener('focusout', onFocusOut);
    viewport.addEventListener('resize', update);
    window.addEventListener('resize', update);
    update();

    return () => {
      clearTimeout(settleTimer);
      document.removeEventListener('focusin', update);
      document.removeEventListener('focusout', onFocusOut);
      viewport.removeEventListener('resize', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  return isOpen;
};
